import * as React from 'react';
import {FunctionComponent, useState} from 'react';
import {Button, Row, Icon, Modal} from '@flexe/ui-components';
import DropDown, {DropDownColor, DropDownOption, DropDownStyle} from '../DropDown';
import CycleCountsService from '../services/CycleCountsService';
import {ErrorCard} from '../../warehouse/outbound-shipments/shared/ErrorCard';
import {CycleCountDetails, CycleCountReasonCode} from './CycleCountInterfaces';

export interface SingleStepCompleteCycleCountModalProps {
  isOpen: boolean;
  toggleModal: () => void;
  cycleCount: CycleCountDetails;
  cycleCountReasonCodes: CycleCountReasonCode[];
  cycleCountsService: CycleCountsService;
  loadCycleCount: () => Promise<void>;
}

export const SingleStepCompleteCycleCountModal: FunctionComponent<SingleStepCompleteCycleCountModalProps> = ({
  isOpen,
  toggleModal,
  cycleCount,
  cycleCountReasonCodes,
  cycleCountsService,
  loadCycleCount
}) => {
  const [selectedReason, setSelectedReason] = useState<DropDownOption>();
  const [editNotes, setEditNotes] = useState<string>('');
  const [purchaseOrder, setPurchaseOrder] = useState<string>('');
  const [isPoRequired, setIsPoRequired] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [reasonCodeButtonText, setReasonCodeButtonText] = useState<string>('+ Reason For Count');
  const [numberOfLocations, setNumberOfLocations] = useState<number>(0);
  const [numberOfItems, setNumberOfItems] = useState<number>(0);
  const [numberOfTotalUnits, setNumberOfTotalUnits] = useState<number>(0);
  const [errors, setErrors] = React.useState<string[]>(null);

  const calculateCountedNumbers = () => {
    const countedLocationIds: number[] = [];
    const countedItemIds: number[] = [];
    let totalUnitsCounted = 0;
    if (cycleCount) {
      for (const item of cycleCount.cycleCountItems) {
        countedItemIds.push(item?.inventory?.id);
        countedLocationIds.push(item?.location?.id);
        totalUnitsCounted += item?.countedQuantities.each || item?.countedQuantities.carton;
      }
    }
    setNumberOfLocations(Array.from(new Set(countedLocationIds)).filter(Number).length);
    setNumberOfItems(Array.from(new Set(countedItemIds)).filter(Number).length);
    setNumberOfTotalUnits(totalUnitsCounted || 0);
  };

  React.useEffect(() => {
    calculateCountedNumbers();
  }, [cycleCount]);

  const dropDownOptions: DropDownOption[] = cycleCountReasonCodes.map((code) => ({
    name: code.name,
    value: code.value
  }));
  const reasonsThatRequirePO: string[] = ['vendor_damaged', 'vendor_overage', 'adjust_past_receipt'];

  const enableCompleteButton = () => {
    if (isPoRequired) {
      return !!(selectedReason?.value && editNotes && purchaseOrder);
    }
    return !!(selectedReason?.value && editNotes);
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    const response = await cycleCountsService.completeCycleCount(
      Number(cycleCount.id),
      cycleCount.version,
      selectedReason.value,
      editNotes,
      purchaseOrder
    );
    if (response.errors.length > 0) {
      const errs = cycleCountsService.processErrors(response.errors);
      setErrors(errs);
    } else {
      setErrors([]);
      toggleModal();
      await loadCycleCount();
    }
    setIsSubmitting(false);
  };

  const handleDropDownSelect = (reason: DropDownOption) => {
    enableCompleteButton();
    setSelectedReason(reason);
    setIsPoRequired(reasonsThatRequirePO.includes(reason?.value as string));
    setReasonCodeButtonText('');
  };

  const handleEditNoteChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditNotes(event.target.value);
  };

  const handleEnterPo = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPurchaseOrder(event.target.value);
  };

  return (
    <Modal title="Complete Cycle Count" size="medium" isOpen={isOpen} toggleModal={toggleModal}>
      <React.Fragment>
        {errors &&
          errors.length > 0 &&
          errors.map((error, idx) => {
            return (
              <ErrorCard key={idx}>
                <p>{error}</p>
              </ErrorCard>
            );
          })}
        <p>
          On-hand inventory will be immediately updated to match your entries. Please verify that you are submitting
          accurate counts.
        </p>
        <div>
          <DropDown
            options={dropDownOptions}
            selected={selectedReason}
            style={DropDownStyle.modal}
            onSelect={handleDropDownSelect}
            prefixText={reasonCodeButtonText}
            color={DropDownColor.white}
          />
        </div>
        {isPoRequired && (
          <div style={{marginTop: '20px', marginBottom: '5px'}}>
            <div style={{marginBottom: '5px'}}>PURCHASE ORDER NUMBER</div>
            <input type="text" className="form-control" aria-label="purchaseOrderId" onChange={handleEnterPo} />
          </div>
        )}
        <div style={{display: 'flex', flexDirection: 'column', marginTop: '20px'}}>
          DETAILS
          <div style={{marginTop: '5px', display: 'flex', flexDirection: 'column'}}>
            <div style={{display: 'flex', flexDirection: 'row'}}>
              <label>Locations Counted:</label>
              <div data-testid="locations-counted" style={{marginLeft: '5px'}}>
                {numberOfLocations}
              </div>
            </div>
            <div style={{display: 'flex', flexDirection: 'row'}}>
              <label>SKUs Counted:</label>
              <div data-testid="items-counted" style={{marginLeft: '5px'}}>
                {numberOfItems}
              </div>
            </div>
            <div style={{display: 'flex', flexDirection: 'row'}}>
              <label>Total Units Counted:</label>
              <div data-testid="total-units-counted" style={{marginLeft: '5px'}}>
                {numberOfTotalUnits}
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            marginTop: '10px',
            marginBottom: '10px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          NOTES
          <textarea
            style={{minHeight: '200px', marginTop: '10px'}}
            name="complete-cycle-count-notes"
            onChange={handleEditNoteChange}
          />
        </div>
        <div className="pull-right align-baseline">
          <Button onPress={handleComplete} visualType="primary" isDisabled={!enableCompleteButton()}>
            <Row margin={['0', '100']} alignmentHorizontal="center">
              {isSubmitting && (
                <div className="spinner">
                  <Icon icon="spinner" animation="spin" label="Loading" />
                </div>
              )}
              <span style={{visibility: isSubmitting ? 'hidden' : 'visible'}}>{'Complete Count'}</span>
            </Row>
          </Button>
        </div>
      </React.Fragment>
    </Modal>
  );
};

export default SingleStepCompleteCycleCountModal;
